import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Collapse,
  Input,
  List,
  Modal,
  Popconfirm,
  Space,
  Tag,
  Typography
} from "antd";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Success } from "../../../commons/Messages/Messages";
import { getRules, resetRules, saveRules } from "./api";

/**
 * The programme rules behind every estimate and checklist: knowledge date, sources and validity
 * per programme, and — for the owner — an editor for the company's own copy (JSON) for when a
 * programme changes before the shipped rules are updated.
 */
const RulesModal = ({ open, onClose, onChanged }) => {
  const { t } = useTranslation("dashboard");
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [editing, setEditing] = useState(false);
  const [errors, setErrors] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () =>
    getRules()
      .then(res => {
        setData(res.data);
        setText(JSON.stringify(res.data.rules, null, 2));
        setErrors([]);
      })
      .catch(generalErrorAlert);

  useEffect(() => {
    if (open) {
      setEditing(false);
      load();
    }
  }, [open]);

  const save = async () => {
    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch (error) {
      setErrors([t("This is not valid JSON: {{error}}", { error: error.message })]);
      return;
    }
    setBusy(true);
    try {
      await saveRules(parsed);
      Success(t("Saved"));
      setEditing(false);
      await load();
      onChanged?.();
    } catch (error) {
      if (error?.response?.data?.details) setErrors(error.response.data.details);
      else generalErrorAlert(error);
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    setBusy(true);
    try {
      await resetRules();
      Success(t("The shipped rules apply again."));
      await load();
      onChanged?.();
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setBusy(false);
    }
  };

  const rules = data?.rules;
  return (
    <Modal
      rootClassName="funding-page"
      open={open}
      width={860}
      title={t("Funding rules")}
      onCancel={onClose}
      footer={
        editing
          ? [
              <Button key="cancel" onClick={() => setEditing(false)}>
                {t("Cancel")}
              </Button>,
              <Button key="save" type="primary" loading={busy} onClick={save}>
                {t("Save")}
              </Button>
            ]
          : [
              data?.can?.edit && data?.custom && (
                <Popconfirm key="reset" title={t("Use the shipped rules again?")} onConfirm={reset}>
                  <Button loading={busy}>{t("Reset to shipped rules")}</Button>
                </Popconfirm>
              ),
              data?.can?.edit && (
                <Button key="edit" onClick={() => setEditing(true)}>
                  {t("Edit rules")}
                </Button>
              ),
              <Button key="close" type="primary" onClick={onClose}>
                {t("Close")}
              </Button>
            ].filter(Boolean)
      }
    >
      {rules && (
        <>
          <Alert
            type="warning"
            showIcon
            className="mb-3"
            message={t(
              "Funding programmes change often. Check every value against the official sources before advising a customer."
            )}
            description={
              <>
                <div>{rules.asOf}</div>
                <div className="mt-1">
                  {t("Rules version")}: <b>{rules.version}</b>{" "}
                  {data.custom ? (
                    <Tag color="blue">{t("Own rules")}</Tag>
                  ) : (
                    <Tag>{t("Shipped rules")}</Tag>
                  )}
                </div>
              </>
            }
          />
          {data.invalid && (
            <Alert
              type="error"
              showIcon
              className="mb-3"
              message={t("Your own rules are no longer valid; the shipped rules apply.")}
            />
          )}
          {editing ? (
            <>
              <Typography.Paragraph type="secondary" className="fu-small">
                {t(
                  "Amounts are in cents, rates in percent. Cases are recalculated with these rules until their application is filed."
                )}
              </Typography.Paragraph>
              {errors.length > 0 && (
                <Alert
                  type="error"
                  className="mb-2"
                  message={t("The rules are not valid.")}
                  description={
                    <ul className="fu-errors">
                      {errors.map(e => (
                        <li key={e}>{e}</li>
                      ))}
                    </ul>
                  }
                />
              )}
              <Input.TextArea
                className="fu-json"
                value={text}
                onChange={event => setText(event.target.value)}
                autoSize={{ minRows: 16, maxRows: 30 }}
                spellCheck={false}
              />
            </>
          ) : (
            <Collapse
              items={rules.programs.map(p => ({
                key: p.key,
                label: (
                  <Space wrap>
                    <span>{p.name}</span>
                    <Tag>{p.agency}</Tag>
                  </Space>
                ),
                children: (
                  <div className="fu-rule">
                    <Typography.Paragraph>{p.applicant}</Typography.Paragraph>
                    <Typography.Paragraph className="fu-small">
                      {t("Source")}: {p.source?.title}
                      {p.source?.url && (
                        <>
                          {" · "}
                          <a href={p.source.url} target="_blank" rel="noreferrer">
                            {p.source.url}
                          </a>
                        </>
                      )}
                      <br />
                      {t("Valid from")}: {p.source?.validFrom}
                    </Typography.Paragraph>
                    {p.source?.verify && (
                      <Alert type="info" showIcon className="mb-2" message={p.source.verify} />
                    )}
                    {p.rates && (
                      <Typography.Paragraph>
                        {t("Base rate {{base}} %, maximum {{max}} %", {
                          base: p.rates.basePercent,
                          max: p.rates.maxPercent
                        })}
                      </Typography.Paragraph>
                    )}
                    {p.caps?.note && (
                      <Typography.Paragraph className="fu-small">
                        {p.caps.note}
                      </Typography.Paragraph>
                    )}
                    {p.tax?.note && (
                      <Typography.Paragraph className="fu-small">{p.tax.note}</Typography.Paragraph>
                    )}
                    <List
                      size="small"
                      header={t("Measures and technical minimums")}
                      dataSource={p.measures || []}
                      renderItem={m => (
                        <List.Item>
                          <div>
                            <b>{m.label}</b>
                            {(m.requirements || []).map(r => (
                              <div key={r.key} className="fu-small">
                                {r.label}
                                {r.max !== undefined ? ` ≤ ${r.max} ${r.unit || ""}` : ""}
                                {r.min !== undefined ? ` ≥ ${r.min} ${r.unit || ""}` : ""}
                                {r.verify ? ` (${t("to verify")})` : ""}
                              </div>
                            ))}
                          </div>
                        </List.Item>
                      )}
                    />
                  </div>
                )
              }))}
            />
          )}
        </>
      )}
    </Modal>
  );
};

export default RulesModal;
